import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePostHog } from 'posthog-js/react';

import { importFreshBudget, pushModal } from 'loot-core/client/actions';

import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from '../../../hooks/useNavigate';
import { AnimatedLoading } from '../../../icons/AnimatedLoading';
import AIEmpoweredOnboarding from '../../../icons/v2/AIEmpoweredOnboarding.svg';
import { useResponsive } from '../../../ResponsiveProvider';
import { theme } from '../../../style';
import { Button, ButtonWithLoading } from '../../common/Button';
import { Card } from '../../common/Card';
import { Text } from '../../common/Text';
import { View } from '../../common/View';

import { OnboardingMintModal } from './OnboardingMintModal';
import { useSessionFlag } from '../../../hooks/useSessionFlag';

export function OnboardingPaid({}: {}) {
  const navigate = useNavigate();
  const [showMintModal, setShowMintModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const { isNarrowWidth, atLeastMediumWidth } = useResponsive();
  const { refreshAuthState } = useAuth();
  const [postAuth, setPostAuth] = useSessionFlag('postAuth');
  const posthog = usePostHog();

  const initAuthState = async () => {
    const state = await refreshAuthState();
    if (!state.isAuthenticated) {
      navigate('/signup');
      return;
    }
  };

  useEffect(() => {
    initAuthState();

    // handle ynab opening
    if (postAuth === 'ynab') {
      dispatch(pushModal('import-ynab5'));
      setPostAuth(null);
    }
  }, []);

  const tryTheAppHandler = () => {
    posthog?.capture('Onboarding: Try the App clicked');
    setSubmitting(true);
    dispatch(importFreshBudget({ isEntirelySkipped: true, skipToAccounts: true }));
  };

  const startAiOnboarding = () => {
    posthog?.capture('Onboarding: Link Account clicked');
    navigate('/onboarding/start');
  };

  return (
    <Card
      style={{
        padding: 20,
        textAlign: 'center',
        maxWidth: 856,
        opacity: submitting ? 0.9 : 1,
      }}
    >
      {submitting && (
        <View
          style={{
            position: 'absolute',
            top: -20,
            left: -20,
            bottom: -20,
            right: -20,
            zIndex: 11000,
            justifyContent: 'center',
          }}
        >
          <AnimatedLoading width={40} color={theme.pageTextDark} />
        </View>
      )}
      <View style={{ gap: 16 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: isNarrowWidth ? 'column' : 'row',
            flexWrap: 'wrap',
            gap: 20,
            padding: '0 16px',
            flex: 1,
          }}
        >
          <View style={{ flexDirection: 'row' }}>
            {atLeastMediumWidth && (
              <img
                src={AIEmpoweredOnboarding}
                style={{
                  flex: 1,
                  objectFit: 'contain',
                  maxWidth: 350,
                }}
              />
            )}
            <View
              style={{
                alignItems: 'center',
                marginLeft: atLeastMediumWidth ? 20 : 0,
                gap: 20,
                alignSelf: 'center',
              }}
            >
              <h2 style={{ marginBottom: 0 }}>
                Let's Get Your Finances Organized
              </h2>
              <Text style={{ fontSize: 16, color: theme.pageTextLight }}>
                Get started right away or connect your accounts for the full
                experience. Track spending, manage budgets, and take control of
                your financial future.
              </Text>
              <View
                style={{
                  flexDirection: 'row',
                  gap: 12,
                  justifyContent: 'center',
                }}
              >
                <ButtonWithLoading
                  type="primary"
                  onClick={tryTheAppHandler}
                  loading={submitting}
                >
                  Try the App
                </ButtonWithLoading>
                <Button type="normal" onClick={startAiOnboarding}>
                  Link Account
                </Button>
              </View>
            </View>
          </View>
        </div>
        <div
          style={{
            borderTop: '1px solid #CCC',
            width: '50%',
            margin: '0 auto',
          }}
        ></div>
        <View>
          <Button
            type="bare"
            style={{
              color: theme.pageTextLight,
              cursor: 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
            onClick={e => {
              e.preventDefault();
              dispatch(pushModal('import-ynab5'));
            }}
            disabled={submitting}
          >
            <span style={{ verticalAlign: 'middle', marginRight: 6 }}>
              Import from
            </span>
            <span style={{ verticalAlign: 'middle' }}>
              <img
                width={50}
                src="/images/ynab-logo.png"
                alt="ynab-logo"
                style={{ display: 'inline-flex' }}
              />
            </span>
          </Button>
          <Button
            type="bare"
            style={{
              color: theme.pageTextLight,
              gap: 6,
              display: 'block',
              cursor: 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
            onClick={() => setShowMintModal(true)}
            disabled={submitting}
          >
            <span style={{ verticalAlign: 'middle', marginRight: 6 }}>
              Import from
            </span>
            <span style={{ verticalAlign: 'middle' }}>
              <img
                width={50}
                src="/images/mint-logo.png"
                alt="mint-logo"
                style={{ display: 'inline-flex' }}
              />
            </span>
          </Button>
          <Button
            type="bare"
            style={{
              color: theme.pageTextLight,
              cursor: 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
            onClick={e => {
              e.preventDefault();
              dispatch(pushModal('import-actual'));
            }}
            disabled={submitting}
          >
            Import from Good Steward
          </Button>
        </View>
      </View>
      {showMintModal && (
        <OnboardingMintModal onClose={() => setShowMintModal(false)} />
      )}
    </Card>
  );
}
